import React from 'react';
import { Box, Typography } from '@mui/material';
import ClassificacaoBadge, { getClassificacaoColor } from './ClassificacaoBadge';

interface ScoreGaugeProps {
  value: number;
  maxValue?: number;
  size?: 'sm' | 'md' | 'lg';
  label?: string;
  showClassificacao?: boolean;
}

const sizeMap = { sm: 64, md: 96, lg: 128 };

const getClassificacao = (pct: number): string => {
  if (pct >= 75) return 'Otimo';
  if (pct >= 50) return 'Bom';
  if (pct >= 25) return 'Suficiente';
  return 'Regular';
};

const ScoreGauge: React.FC<ScoreGaugeProps> = ({
  value,
  maxValue = 100,
  size = 'md',
  label,
  showClassificacao = false,
}) => {
  const px = sizeMap[size];
  const strokeWidth = size === 'sm' ? 6 : size === 'md' ? 8 : 10;
  const radius = (px - strokeWidth) / 2;
  const cx = px / 2;
  const cy = px / 2;

  const pct = maxValue > 0 ? Math.min((value / maxValue) * 100, 100) : 0;
  const classificacao = getClassificacao(pct);
  const color = getClassificacaoColor(classificacao);

  // Semicircle arc from left (180deg) to right (0deg)
  const halfCircumference = Math.PI * radius;
  const offset = halfCircumference - (pct / 100) * halfCircumference;

  const valueFontSize = size === 'sm' ? '0.85rem' : size === 'md' ? '1.25rem' : '1.6rem';
  const labelFontSize = size === 'sm' ? '0.6rem' : size === 'md' ? '0.7rem' : '0.8rem';

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      <Box sx={{ position: 'relative', width: px, height: cx + 4 }}>
        <svg width={px} height={cx + 4} viewBox={`0 0 ${px} ${cx + 4}`}>
          {/* Background arc */}
          <path
            d={`M ${strokeWidth / 2} ${cy} A ${radius} ${radius} 0 0 1 ${px - strokeWidth / 2} ${cy}`}
            fill="none"
            stroke="#e0e0e0"
            strokeWidth={strokeWidth}
            strokeLinecap="round"
          />
          {/* Foreground arc */}
          <path
            d={`M ${strokeWidth / 2} ${cy} A ${radius} ${radius} 0 0 1 ${px - strokeWidth / 2} ${cy}`}
            fill="none"
            stroke={color}
            strokeWidth={strokeWidth}
            strokeLinecap="round"
            strokeDasharray={halfCircumference}
            strokeDashoffset={offset}
            style={{ transition: 'stroke-dashoffset 0.6s ease, stroke 0.3s ease' }}
          />
        </svg>
        {/* Center value */}
        <Box
          sx={{
            position: 'absolute',
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            justifyContent: 'center',
          }}
        >
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: valueFontSize,
              color,
              lineHeight: 1,
              transition: 'color 0.3s ease',
            }}
          >
            {Math.round(value)}
          </Typography>
        </Box>
      </Box>
      {label && (
        <Typography
          variant="caption"
          sx={{ color: 'text.secondary', fontSize: labelFontSize, mt: 0.25, textAlign: 'center' }}
        >
          {label}
        </Typography>
      )}
      {showClassificacao && (
        <Box sx={{ mt: 0.5 }}>
          <ClassificacaoBadge classificacao={classificacao} variant="chip" size="small" />
        </Box>
      )}
    </Box>
  );
};

export default ScoreGauge;
